import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import {
    FileVideoIcon,
    ArticleNyTimesIcon,
    BellSimpleIcon,
} from '@phosphor-icons/react';
import {
    AlertFormData,
    NewAlertRule,
    AlertRuleCreationStep,
    AlertRuleCreationFormField,
    AlertRuleActionType,
    FullAlertRule,
} from '@/types/alert';
import { PromptType } from '@/types/prompt';
import { AppRoutes } from '@/constants/routes';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { createFormDataFromAlertRule } from '@/utils/alert-rules';
import { EngineService } from '@/services/engine';
import { revalidateAlertUnitCapacity } from '@/features/SettingsView/hooks/useConfiguration';
import { AccordionStepper, Step, toast } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { getApiErrorMessage } from '@/utils/error-messages';
import { SourceType } from '@/types/source';
import {
    countedAlertUnits,
    selectionExceedsAllowedAlertUnits,
    spendingSourceCount,
} from './alert-unit-limit';
import { AlertsLimit } from './components/AlertsLimit';
import { SourceSelectionStep } from './components/SourceSelectionStep';
import { PromptDefinitionStep } from './components/PromptDefinitionStep';
import { ActionConfigurationStep } from './components/ActionConfigurationStep';
import {
    VALIDATION_LIMITS,
    authorizationPayload,
    nonEmptyHeaders,
} from './validation';

function sameSourceIds(
    left: readonly string[],
    right: readonly string[],
): boolean {
    if (left.length !== right.length) return false;
    const rightIds = new Set(right);
    return left.every((sourceId) => rightIds.has(sourceId));
}

function stepDiffersFromSaved(
    step: AlertRuleCreationStep,
    current: AlertFormData,
    saved: AlertFormData,
): boolean {
    if (step === AlertRuleCreationStep.SELECT_SOURCES) {
        return !sameSourceIds(current.sources, saved.sources);
    }
    if (step === AlertRuleCreationStep.DEFINE_PROMPT) {
        return (
            current.prompt !== saved.prompt ||
            (current.attachment?.url ?? '') !== (saved.attachment?.url ?? '')
        );
    }
    return false;
}

export interface SimulationProps {
    run: (params: {
        promptText: string;
        sourceIds: string[];
        attachment?: { url: string; type?: string };
    }) => void;
    stop: () => void;
    isRunning: boolean;
    onConfidenceThresholdChange: (threshold: number | undefined) => void;
    onPreviewConfidenceThreshold: (threshold: number | null) => void;
}

interface AlertCreationProps {
    alertRuleId?: string;
    initialData?: FullAlertRule;
    simulation?: SimulationProps;
}

export function AlertRuleCreation({
    alertRuleId,
    initialData,
    simulation,
}: AlertCreationProps) {
    const editMode = !!alertRuleId;
    const navigate = useNavigate();
    const { t } = useTranslation(I18nNamespace.ALERT_RULES);
    const [activeStep, setActiveStep] = useState<
        AlertRuleCreationStep | undefined
    >(AlertRuleCreationStep.SELECT_SOURCES);
    const [submitting, setSubmitting] = useState(false);
    const [stepsLeftByNext, setStepsLeftByNext] = useState<
        ReadonlySet<AlertRuleCreationStep>
    >(() => new Set());

    const [formData, setFormData] = useState<AlertFormData>(() =>
        createFormDataFromAlertRule(initialData),
    );
    const [errors, setErrors] = useState<
        Partial<Record<keyof AlertFormData, string>>
    >({});
    const [alertUnitCapacity, setAlertUnitCapacity] = useState<
        | Awaited<ReturnType<typeof EngineService.getAlertUnitCapacity>>
        | undefined
    >();
    useEffect(() => {
        let cancelled = false;
        setAlertUnitCapacity(undefined);
        void EngineService.getAlertUnitCapacity(alertRuleId).then(
            (capacity) => {
                if (!cancelled) setAlertUnitCapacity(capacity);
            },
        );
        return () => {
            cancelled = true;
        };
    }, [alertRuleId]);
    const capKnown = alertUnitCapacity !== undefined;
    const maxAlertUnits = capKnown ? alertUnitCapacity.maxAlertUnits : null;
    const usedElsewhere = alertUnitCapacity?.usedAlertUnits ?? 0;
    const { data: liveCameraIds } = useSWR(
        [CACHE_KEYS.SOURCES_PREFIX, 'ids', SourceType.CAMERA],
        () => EngineService.getSourceIds({ type: SourceType.CAMERA }),
    );
    const selectedSourceIds = formData[AlertRuleCreationFormField.SOURCES];
    const ruleIsActive = formData[AlertRuleCreationFormField.ACTIVE];
    const liveSourceIds = liveCameraIds ? new Set(liveCameraIds) : null;
    const selectedSpend = ruleIsActive
        ? spendingSourceCount(selectedSourceIds, liveSourceIds)
        : 0;
    const usedAlertUnits = countedAlertUnits(usedElsewhere, selectedSpend);
    const effectiveMaxAlertUnits = ruleIsActive
        ? capKnown
            ? maxAlertUnits
            : undefined
        : null;

    const savedForm = createFormDataFromAlertRule(initialData);
    const savedSourceIds =
        editMode && savedForm[AlertRuleCreationFormField.ACTIVE]
            ? savedForm[AlertRuleCreationFormField.SOURCES]
            : null;
    const selectionBlocksSave = selectionExceedsAllowedAlertUnits(
        usedElsewhere,
        selectedSpend,
        effectiveMaxAlertUnits,
        savedSourceIds,
        selectedSourceIds,
    );

    const loadedSourceKey = (initialData?.sources ?? [])
        .map((source) => source.id)
        .join('\0');

    useEffect(() => {
        setFormData(createFormDataFromAlertRule(initialData));
        setStepsLeftByNext(new Set());
    }, [initialData, loadedSourceKey]);

    useEffect(() => {
        if (!simulation?.onConfidenceThresholdChange) return;
        if (formData.actions.length === 0) {
            simulation.onConfidenceThresholdChange(undefined);
        } else {
            const minThreshold = Math.min(
                ...formData.actions.map((action) => action.confidenceThreshold),
            );
            simulation.onConfidenceThresholdChange(minThreshold);
        }
    }, [formData.actions, simulation?.onConfidenceThresholdChange]);

    const clearFieldError = (field: keyof AlertFormData) => {
        setErrors((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const updateField = <K extends keyof AlertFormData>(
        field: K,
        value: AlertFormData[K],
    ) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        clearFieldError(field);
    };

    const updateSources = (update: (previous: string[]) => string[]) => {
        setFormData((prev) => ({
            ...prev,
            [AlertRuleCreationFormField.SOURCES]: update(
                prev[AlertRuleCreationFormField.SOURCES],
            ),
        }));
        clearFieldError(AlertRuleCreationFormField.SOURCES);
    };

    const validateStep = (stepId: AlertRuleCreationStep): boolean => {
        const newErrors: Partial<Record<keyof AlertFormData, string>> = {};
        let selectionOverCap = false;

        switch (stepId) {
            case AlertRuleCreationStep.SELECT_SOURCES:
                if (formData[AlertRuleCreationFormField.SOURCES].length === 0) {
                    newErrors[AlertRuleCreationFormField.SOURCES] = t(
                        'form.validation.selectSource',
                    );
                }
                selectionOverCap = selectionBlocksSave;
                break;

            case AlertRuleCreationStep.DEFINE_PROMPT:
                const trimmedPrompt =
                    formData[AlertRuleCreationFormField.PROMPT].trim();
                const hasAttachment = !!formData.attachment;

                if (!hasAttachment && !trimmedPrompt) {
                    newErrors[AlertRuleCreationFormField.PROMPT] = t(
                        'form.validation.promptRequired',
                    );
                } else if (
                    !hasAttachment &&
                    trimmedPrompt.length < VALIDATION_LIMITS.MIN_PROMPT_LENGTH
                ) {
                    newErrors[AlertRuleCreationFormField.PROMPT] = t(
                        'form.validation.promptMinLength',
                        { min: VALIDATION_LIMITS.MIN_PROMPT_LENGTH },
                    );
                }
                break;

            case AlertRuleCreationStep.CONFIGURE_ACTIONS:
                if (formData[AlertRuleCreationFormField.ACTIONS].length === 0) {
                    newErrors[AlertRuleCreationFormField.ACTIONS] = t(
                        'form.validation.configureAction',
                    );
                }

                for (const action of formData[
                    AlertRuleCreationFormField.ACTIONS
                ]) {
                    if (
                        action.confidenceThreshold <
                            VALIDATION_LIMITS.MIN_CONFIDENCE_THRESHOLD ||
                        action.confidenceThreshold >
                            VALIDATION_LIMITS.MAX_CONFIDENCE_THRESHOLD
                    ) {
                        newErrors[AlertRuleCreationFormField.ACTIONS] = t(
                            'form.validation.confidenceThreshold',
                            {
                                min: VALIDATION_LIMITS.MIN_CONFIDENCE_THRESHOLD,
                                max: VALIDATION_LIMITS.MAX_CONFIDENCE_THRESHOLD,
                            },
                        );
                        break;
                    }

                    if (
                        action.type === AlertRuleActionType.WHATSAPP &&
                        !action.recipient?.phone
                    ) {
                        newErrors[AlertRuleCreationFormField.ACTIONS] = t(
                            'form.validation.whatsappPhoneRequired',
                        );
                        break;
                    }
                }
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0 && !selectionOverCap;
    };

    const setStepConfirmed = (
        step: AlertRuleCreationStep,
        confirmed: boolean,
    ) => {
        setStepsLeftByNext((current) => {
            if (current.has(step) === confirmed) return current;
            const nextConfirmed = new Set(current);
            if (confirmed) {
                nextConfirmed.add(step);
            } else {
                nextConfirmed.delete(step);
            }
            return nextConfirmed;
        });
    };

    const markStepLeftByNext = (step: AlertRuleCreationStep) => {
        const confirmStep =
            step === AlertRuleCreationStep.SELECT_SOURCES
                ? formData[AlertRuleCreationFormField.SOURCES].length > 0
                : !editMode || stepDiffersFromSaved(step, formData, savedForm);
        setStepConfirmed(step, confirmStep);
    };

    const leaveStep = (step: AlertRuleCreationStep | undefined) => {
        if (!step) return;
        if (step === AlertRuleCreationStep.SELECT_SOURCES) {
            setStepConfirmed(
                step,
                formData[AlertRuleCreationFormField.SOURCES].length > 0,
            );
            return;
        }
        setStepConfirmed(step, false);
    };

    const handleNext = (
        currentStep: AlertRuleCreationStep,
        nextStep: AlertRuleCreationStep,
    ) => {
        if (validateStep(currentStep)) {
            markStepLeftByNext(currentStep);
            setActiveStep(nextStep);
        }
    };

    const handleRunSimulation = useCallback(() => {
        simulation?.run({
            promptText: formData[AlertRuleCreationFormField.PROMPT],
            sourceIds: formData[AlertRuleCreationFormField.SOURCES],
            attachment: formData.attachment,
        });
    }, [formData, simulation?.run]);

    const handleSubmit = async () => {
        const steps = [
            AlertRuleCreationStep.SELECT_SOURCES,
            AlertRuleCreationStep.DEFINE_PROMPT,
            AlertRuleCreationStep.CONFIGURE_ACTIONS,
        ];

        for (const step of steps) {
            if (!validateStep(step)) {
                setActiveStep(step);
                return;
            }
        }

        if (formData[AlertRuleCreationFormField.ACTIVE] && !capKnown) {
            toast.error(
                t('errors.organization.alert_unit_count_unavailable', {
                    ns: I18nNamespace.SETTINGS,
                }),
            );
            return;
        }

        setSubmitting(true);
        try {
            const payload: Partial<NewAlertRule> = {
                name:
                    formData[AlertRuleCreationFormField.NAME] ||
                    `Alert for "${formData[AlertRuleCreationFormField.PROMPT]}"`,
                sources: formData[AlertRuleCreationFormField.SOURCES],
                prompts: [
                    {
                        text: formData[AlertRuleCreationFormField.PROMPT],
                        type: PromptType.NORMAL,
                        attachments: formData.attachment
                            ? [
                                  {
                                      url: formData.attachment.url,
                                      type:
                                          formData.attachment.type || 'OBJECT',
                                  },
                              ]
                            : [],
                    },
                ],
                actions: formData[AlertRuleCreationFormField.ACTIONS].map(
                    (action) => ({
                        ...(editMode && action.id && { id: action.id }),
                        type: action.type,
                        threshold: action.confidenceThreshold,
                        ...(action.recipient?.phone && {
                            recipient: {
                                phone: action.recipient.phone,
                            },
                        }),
                        ...(action.webhook?.url && {
                            webhook: {
                                url: action.webhook.url,
                                headers: nonEmptyHeaders(
                                    action.webhook.headers,
                                ),
                                authorization: authorizationPayload(
                                    action.webhook.authorization,
                                ),
                            },
                        }),
                    }),
                ),
                active: formData[AlertRuleCreationFormField.ACTIVE],
            };

            if (editMode && alertRuleId) {
                await EngineService.updateAlertRule(alertRuleId, payload);
                toast.success(t('toast.updateSuccess'));
            } else {
                await EngineService.createAlertRule(payload);
                toast.success(t('toast.createSuccess'));
            }
            await revalidateAlertUnitCapacity();

            navigate(AppRoutes.ALERT_RULES);
        } catch (error) {
            toast.error(
                getApiErrorMessage(
                    error,
                    editMode ? t('toast.updateError') : t('toast.createError'),
                ),
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AccordionStepper
            value={activeStep}
            onValueChange={(value) => {
                leaveStep(activeStep);
                setActiveStep(value as AlertRuleCreationStep | undefined);
            }}
        >
            <Step
                id={AlertRuleCreationStep.SELECT_SOURCES}
                title={t('form.steps.selectSources.title')}
                description={t('form.steps.selectSources.description')}
                icon={<FileVideoIcon size={20} />}
                isCompleted={
                    activeStep !== AlertRuleCreationStep.SELECT_SOURCES &&
                    stepsLeftByNext.has(AlertRuleCreationStep.SELECT_SOURCES)
                }
                headerAccessory={
                    activeStep === AlertRuleCreationStep.SELECT_SOURCES &&
                    capKnown &&
                    maxAlertUnits !== null ? (
                        <AlertsLimit
                            used={usedAlertUnits}
                            max={maxAlertUnits}
                        />
                    ) : null
                }
            >
                <SourceSelectionStep
                    selectedSourceIds={selectedSourceIds}
                    maxAlertUnits={effectiveMaxAlertUnits}
                    usedElsewhere={usedElsewhere}
                    savedSourceIds={savedSourceIds}
                    onSourcesChange={updateSources}
                    onNext={() =>
                        handleNext(
                            AlertRuleCreationStep.SELECT_SOURCES,
                            AlertRuleCreationStep.DEFINE_PROMPT,
                        )
                    }
                    error={errors[AlertRuleCreationFormField.SOURCES]}
                />
            </Step>

            <Step
                id={AlertRuleCreationStep.DEFINE_PROMPT}
                title={t('form.steps.defineAlert.title')}
                description={t('form.steps.defineAlert.description')}
                icon={<ArticleNyTimesIcon size={20} />}
                isCompleted={
                    activeStep !== AlertRuleCreationStep.DEFINE_PROMPT &&
                    stepsLeftByNext.has(AlertRuleCreationStep.DEFINE_PROMPT)
                }
            >
                <PromptDefinitionStep
                    prompt={formData[AlertRuleCreationFormField.PROMPT]}
                    onPromptChange={(prompt) =>
                        updateField(AlertRuleCreationFormField.PROMPT, prompt)
                    }
                    attachment={formData.attachment}
                    onAttachmentChange={(attachment) =>
                        updateField('attachment', attachment)
                    }
                    selectedSourceIds={
                        formData[AlertRuleCreationFormField.SOURCES]
                    }
                    onRemoveSource={(id) =>
                        updateSources((previous) =>
                            previous.filter((sourceId) => sourceId !== id),
                        )
                    }
                    onNext={() =>
                        handleNext(
                            AlertRuleCreationStep.DEFINE_PROMPT,
                            AlertRuleCreationStep.CONFIGURE_ACTIONS,
                        )
                    }
                    onRunSimulation={handleRunSimulation}
                    onStopSimulation={() => simulation?.stop()}
                    isSimulating={simulation?.isRunning ?? false}
                    error={errors[AlertRuleCreationFormField.PROMPT]}
                />
            </Step>

            <Step
                id={AlertRuleCreationStep.CONFIGURE_ACTIONS}
                title={t('form.steps.chooseMethod.title')}
                description={t('form.steps.chooseMethod.description')}
                icon={<BellSimpleIcon size={20} />}
            >
                <ActionConfigurationStep
                    actions={formData[AlertRuleCreationFormField.ACTIONS]}
                    editMode={editMode}
                    submitting={submitting}
                    error={errors[AlertRuleCreationFormField.ACTIONS]}
                    onActionsChange={(actions) =>
                        updateField(AlertRuleCreationFormField.ACTIONS, actions)
                    }
                    onSubmit={handleSubmit}
                    onPreviewConfidenceThreshold={
                        simulation?.onPreviewConfidenceThreshold
                    }
                />
            </Step>
        </AccordionStepper>
    );
}
